import React, { useCallback, useEffect, useMemo, useState } from "react";
import { auth, db, storage } from "./firebase";
import { collection, deleteDoc, doc, getDocs, query, updateDoc, where } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { MAJOR_INDIAN_CITIES, SERVICE_CATEGORIES, SERVICE_NAME_OPTIONS } from "./serviceCatalog";
import { optimizeImageForUpload } from "./imageUploadUtils";
import { assertStorageReady, formatUploadError } from "./uploadErrorUtils";
import { EmptyState, PageHeader, StatCard, ToneBadge } from "./petaarUi";

const filterBarStyle = {
  display: "grid",
  gap: 12,
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  alignItems: "center",
};

const cardShellStyle = {
  display: "grid",
  gap: 18,
};

const actionRowStyle = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
};

function visibilityTone(service) {
  const status = String(service.providerStatusSnapshot || "").toLowerCase();
  const isLive = Boolean(service.isPublic) && status === "approved";
  if (isLive) return "success";
  return "warning";
}

function visibilityLabel(service) {
  const status = String(service.providerStatusSnapshot || "").toLowerCase();
  const isLive = Boolean(service.isPublic) && status === "approved";
  if (isLive) return "Live on marketplace";
  if (status && status !== "approved") return `Hidden (${status} provider status)`;
  return "Hidden until admin sync";
}

export default function MyServices({ onBack }) {
  const [services, setServices] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [deletingId, setDeletingId] = useState("");
  const [uploadingByService, setUploadingByService] = useState({});
  const [searchText, setSearchText] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterCity, setFilterCity] = useState("");
  const [filterVisibility, setFilterVisibility] = useState("");
  const [reviewStatsByService, setReviewStatsByService] = useState({});
  const userId = auth.currentUser?.uid;

  const uploadImageFile = async (file) => {
    const uid = auth.currentUser?.uid;
    if (!uid) throw new Error("Login expired. Please login again.");
    await assertStorageReady(storage?.app?.options?.storageBucket || "");
    const optimized = await optimizeImageForUpload(file, {
      maxWidth: 1600,
      maxHeight: 1600,
      quality: 0.82,
      maxOutputBytes: 900 * 1024,
      skipBelowBytes: 220 * 1024,
      outputType: "image/jpeg",
    });
    const safeName = String(optimized.name || file.name || "image")
      .toLowerCase()
      .replace(/[^a-z0-9._-]/g, "-")
      .slice(0, 80);
    const filePath = `serviceImages/${uid}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${safeName}`;
    const fileRef = ref(storage, filePath);
    await uploadBytes(fileRef, optimized, {
      contentType: optimized.type || file.type || "image/jpeg",
      cacheControl: "public,max-age=3600",
    });
    return getDownloadURL(fileRef);
  };

  const categoryOptions = SERVICE_CATEGORIES.map((c) => c.name);
  const serviceOptionsByCategory = useMemo(() => {
    const grouped = {};
    SERVICE_NAME_OPTIONS.forEach((row) => {
      const categoryName = String(row.category || "").trim();
      const serviceName = String(row.name || "").trim();
      if (!categoryName || !serviceName) return;
      if (!Array.isArray(grouped[categoryName])) {
        grouped[categoryName] = [];
      }
      if (!grouped[categoryName].includes(serviceName)) {
        grouped[categoryName].push(serviceName);
      }
    });
    return grouped;
  }, []);

  const fetchServices = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      setLoadError("");
      const email = auth.currentUser?.email || "";
      const [snap, reviewsSnap] = await Promise.all([
        getDocs(query(collection(db, "services"), where("providerId", "==", userId))),
        email
          ? getDocs(query(collection(db, "reviews"), where("providerEmail", "==", email)))
          : Promise.resolve({ docs: [] }),
      ]);
      const list = snap.docs.map((item) => ({ id: item.id, ...item.data() }));
      const totals = {};
      reviewsSnap.docs.forEach((item) => {
        const data = item.data() || {};
        if (data.hidden === true) return;
        const serviceId = String(data.serviceId || "").trim();
        const rating = Number(data.rating);
        if (!serviceId || !Number.isFinite(rating)) return;
        const current = totals[serviceId] || { sum: 0, count: 0 };
        current.sum += rating;
        current.count += 1;
        totals[serviceId] = current;
      });
      const normalized = {};
      Object.entries(totals).forEach(([serviceId, stat]) => {
        normalized[serviceId] = {
          avg: stat.count ? stat.sum / stat.count : 0,
          count: stat.count,
        };
      });
      setReviewStatsByService(normalized);
      setServices(list);
      setDrafts(
        list.reduce((acc, service) => ({
          ...acc,
          [service.id]: {
            title: service.title || "",
            category: service.category || "",
            city: service.city || "",
            price: service.price ?? "",
            availabilityNote: service.availabilityNote || "Mon-Sat",
            imageUrls: Array.isArray(service.imageUrls) ? service.imageUrls : [],
          },
        }), {}),
      );
    } catch (error) {
      setServices([]);
      setReviewStatsByService({});
      setLoadError(error.message || "Unable to load your services right now.");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    fetchServices();
  }, [fetchServices, userId]);

  const saveAvailability = async (serviceId) => {
    try {
      const draft = drafts[serviceId];
      const imageUrls = Array.isArray(draft?.imageUrls) ? draft.imageUrls.slice(0, 6) : [];
      const numericPrice = Number(draft?.price);
      if (!Number.isFinite(numericPrice) || numericPrice < 1) {
        alert("Price must be a valid number and at least ₹1.");
        return;
      }
      await updateDoc(doc(db, "services", serviceId), {
        title: String(draft?.title || "").trim(),
        category: String(draft?.category || "").trim(),
        city: String(draft?.city || "").trim(),
        price: numericPrice,
        availabilityNote: (draft?.availabilityNote || "").trim(),
        imageUrls,
      });
      await fetchServices();
      alert("Service updated successfully.");
    } catch (error) {
      alert(`Failed to save service: ${error.message}`);
    }
  };

  const handlePickImages = async (serviceId, event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    const maxBytes = 12 * 1024 * 1024;
    const currentCount = (drafts[serviceId]?.imageUrls || []).length;
    const onlyImages = files
      .filter((file) => file.type.startsWith("image/") && file.size <= maxBytes)
      .slice(0, Math.max(0, 6 - currentCount));
    if (onlyImages.length < files.length) {
      alert("Some files were skipped. Use image files up to 12MB each, max 6 photos.");
    }
    if (!onlyImages.length) {
      event.target.value = "";
      return;
    }
    try {
      setUploadingByService((prev) => ({ ...prev, [serviceId]: true }));
      const urls = await Promise.all(onlyImages.map(uploadImageFile));
      setDrafts((prev) => ({
        ...prev,
        [serviceId]: {
          ...(prev[serviceId] || {}),
          imageUrls: [...(prev[serviceId]?.imageUrls || []), ...urls].slice(0, 6),
        },
      }));
    } catch (error) {
      alert(`Image upload failed: ${formatUploadError(error, storage?.app?.options?.storageBucket)}`);
    } finally {
      setUploadingByService((prev) => ({ ...prev, [serviceId]: false }));
      event.target.value = "";
    }
  };

  const handleDeleteService = async (serviceId) => {
    const ok = window.confirm("Delete this service? This action cannot be undone.");
    if (!ok) return;
    try {
      setDeletingId(serviceId);
      await deleteDoc(doc(db, "services", serviceId));
      await fetchServices();
      alert("Service deleted.");
    } catch (error) {
      alert(`Failed to delete service: ${error.message}`);
    } finally {
      setDeletingId("");
    }
  };

  const filteredServices = services
    .filter((service) => {
      const q = searchText.trim().toLowerCase();
      if (!q) return true;
      return String(service.title || "").toLowerCase().includes(q);
    })
    .filter((service) => (filterCategory ? String(service.category || "") === filterCategory : true))
    .filter((service) => (filterCity ? String(service.city || "") === filterCity : true))
    .filter((service) => {
      if (!filterVisibility) return true;
      const isLive = visibilityLabel(service).toLowerCase().includes("live");
      if (filterVisibility === "live") return isLive;
      if (filterVisibility === "hidden") return !isLive;
      return true;
    });

  const stats = useMemo(() => {
    const liveCount = services.filter((service) => visibilityLabel(service).toLowerCase().includes("live")).length;
    const featuredCount = services.filter((service) => {
      const untilMs = new Date(service.featuredUntil || 0).getTime();
      return service.isFeatured && untilMs > Date.now();
    }).length;
    const ratings = Object.values(reviewStatsByService).filter((item) => Number(item?.count) > 0);
    const avgRating = ratings.length
      ? ratings.reduce((sum, item) => sum + Number(item.avg || 0), 0) / ratings.length
      : 0;
    return {
      total: services.length,
      liveCount,
      hiddenCount: Math.max(0, services.length - liveCount),
      featuredCount,
      avgRating,
    };
  }, [reviewStatsByService, services]);

  return (
    <div className="petaar-page">
      <PageHeader
        eyebrow="Provider Services"
        title="Manage Your Listings"
        subtitle="Edit pricing, availability, photos, and visibility for every service you offer."
        actions={(
          <div style={actionRowStyle}>
            {onBack ? (
              <button className="petaar-button petaar-button--secondary" onClick={onBack}>
                Back To Dashboard
              </button>
            ) : null}
            <button className="petaar-button" onClick={fetchServices} disabled={loading}>
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        )}
      />

      <div className="petaar-stat-grid" style={{ marginBottom: 24 }}>
        <StatCard label="Total services" value={stats.total} tone="default" meta="All provider listings" />
        <StatCard label="Live listings" value={stats.liveCount} tone="success" meta="Visible to pet owners" />
        <StatCard label="Hidden listings" value={stats.hiddenCount} tone="warning" meta="Need approval or sync" />
        <StatCard label="Featured services" value={stats.featuredCount} tone="accent" meta="Boosted visibility" />
      </div>

      {loadError ? <div className="petaar-notice error" style={{ marginBottom: 16 }}>{loadError}</div> : null}

      <section className="petaar-card" style={{ marginBottom: 24 }}>
        <div className="petaar-section-title">
          <div>
            <div className="petaar-kicker">Filters</div>
            <h3 style={{ fontSize: "1.35rem" }}>Search and segment listings</h3>
          </div>
          <button
            className="petaar-button petaar-button--ghost"
            onClick={() => {
              setSearchText("");
              setFilterCategory("");
              setFilterCity("");
              setFilterVisibility("");
            }}
          >
            Clear Filters
          </button>
        </div>
        <div style={filterBarStyle}>
          <input
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search by service title"
          />
          <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
            <option value="">All categories</option>
            {categoryOptions.map((option) => (
              <option key={`filter-cat-${option}`} value={option}>{option}</option>
            ))}
          </select>
          <select value={filterCity} onChange={(e) => setFilterCity(e.target.value)}>
            <option value="">All cities</option>
            {MAJOR_INDIAN_CITIES.map((city) => (
              <option key={`filter-city-${city}`} value={city}>{city}</option>
            ))}
          </select>
          <select value={filterVisibility} onChange={(e) => setFilterVisibility(e.target.value)}>
            <option value="">All visibility</option>
            <option value="live">Live</option>
            <option value="hidden">Hidden</option>
          </select>
        </div>
      </section>

      {loading ? (
        <div className="petaar-grid" style={{ gap: 16 }}>
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={`service-skeleton-${index}`} className="petaar-skeleton" style={{ height: 240, borderRadius: 20 }} />
          ))}
        </div>
      ) : filteredServices.length === 0 ? (
        <EmptyState
          icon="🧺"
          title="No services found"
          description="Try changing the filters, or add your first service listing so customers can start booking."
          actionLabel={services.length ? "Clear Filters" : "Refresh"}
          onAction={services.length ? () => {
            setSearchText("");
            setFilterCategory("");
            setFilterCity("");
            setFilterVisibility("");
          } : fetchServices}
        />
      ) : (
        <div className="petaar-grid" style={{ gap: 18 }}>
          {filteredServices.map((service) => {
            const draft = drafts[service.id] || {};
            const draftCategory = String(draft.category || "").trim();
            const draftTitle = String(draft.title || "").trim();
            const optionsForCategory = serviceOptionsByCategory[draftCategory] || [];
            const hasLegacyTitle = Boolean(
              draftTitle &&
              optionsForCategory.length &&
              !optionsForCategory.includes(draftTitle),
            );
            const ratingMeta = reviewStatsByService[service.id];

            return (
              <section key={service.id} className="petaar-card" style={cardShellStyle}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap", alignItems: "flex-start" }}>
                  <div style={{ display: "grid", gap: 8 }}>
                    <h3 style={{ fontSize: "1.45rem" }}>{service.title || "Service"}</h3>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                      <ToneBadge tone={visibilityTone(service)}>{visibilityLabel(service)}</ToneBadge>
                      <ToneBadge tone="default">{service.category || "General"}</ToneBadge>
                      <ToneBadge tone="accent">₹{Number(service.price || 0).toLocaleString("en-IN")}</ToneBadge>
                      {service.isFeatured && service.featuredUntil && new Date(service.featuredUntil).getTime() > Date.now() ? (
                        <ToneBadge tone="accent">Featured till {new Date(service.featuredUntil).toLocaleDateString("en-IN")}</ToneBadge>
                      ) : null}
                    </div>
                    <div style={{ color: "var(--muted)", lineHeight: 1.6 }}>
                      {service.city || "No city"} · {service.availabilityNote || "Availability not set"}
                    </div>
                    <div style={{ color: "var(--muted)", fontSize: "0.95rem" }}>
                      Rating: {ratingMeta ? `${Number(ratingMeta.avg || 0).toFixed(1)}/5 (${ratingMeta.count})` : "No reviews yet"}
                    </div>
                  </div>
                </div>

                <div className="petaar-form-grid">
                  <select
                    value={draftTitle}
                    onChange={(e) => setDrafts((prev) => ({
                      ...prev,
                      [service.id]: {
                        ...(prev[service.id] || {}),
                        title: e.target.value,
                      },
                    }))}
                    disabled={!draftCategory}
                  >
                    <option value="">Select service name</option>
                    {hasLegacyTitle ? <option value={draftTitle}>{draftTitle} (legacy)</option> : null}
                    {optionsForCategory.map((serviceName) => (
                      <option key={`${service.id}-name-${serviceName}`} value={serviceName}>
                        {serviceName}
                      </option>
                    ))}
                  </select>

                  <select
                    value={draftCategory}
                    onChange={(e) => {
                      const nextCategory = e.target.value;
                      setDrafts((prev) => {
                        const previousDraft = prev[service.id] || {};
                        const nextCategoryOptions = serviceOptionsByCategory[nextCategory] || [];
                        const previousTitle = String(previousDraft.title || "").trim();
                        return {
                          ...prev,
                          [service.id]: {
                            ...previousDraft,
                            category: nextCategory,
                            title: nextCategoryOptions.includes(previousTitle) ? previousTitle : "",
                          },
                        };
                      });
                    }}
                  >
                    <option value="">Select category</option>
                    {categoryOptions.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>

                  <select
                    value={drafts[service.id]?.city || ""}
                    onChange={(e) => setDrafts((prev) => ({
                      ...prev,
                      [service.id]: {
                        ...(prev[service.id] || {}),
                        city: e.target.value,
                      },
                    }))}
                  >
                    <option value="">Select city</option>
                    {MAJOR_INDIAN_CITIES.map((city) => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </select>

                  <input
                    type="number"
                    min="1"
                    value={drafts[service.id]?.price ?? ""}
                    onChange={(e) => setDrafts((prev) => ({
                      ...prev,
                      [service.id]: {
                        ...(prev[service.id] || {}),
                        price: e.target.value,
                      },
                    }))}
                    placeholder="Price in INR"
                  />
                </div>

                <input
                  value={drafts[service.id]?.availabilityNote || ""}
                  onChange={(e) => setDrafts((prev) => ({
                    ...prev,
                    [service.id]: {
                      ...(prev[service.id] || {}),
                      availabilityNote: e.target.value,
                    },
                  }))}
                  placeholder="Availability note"
                />

                <div className="petaar-card" style={{ padding: 18, display: "grid", gap: 12, background: "var(--surface-alt)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                    <div>
                      <div className="petaar-kicker">Media</div>
                      <h4 style={{ fontSize: "1.1rem" }}>Listing photos</h4>
                    </div>
                    <label htmlFor={`service-images-${service.id}`} className="petaar-button petaar-button--secondary" style={{ cursor: uploadingByService[service.id] ? "not-allowed" : "pointer" }}>
                      {uploadingByService[service.id] ? "Uploading..." : "Upload Images"}
                    </label>
                    <input
                      id={`service-images-${service.id}`}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(event) => handlePickImages(service.id, event)}
                      style={{ display: "none" }}
                    />
                  </div>

                  {(drafts[service.id]?.imageUrls || []).length ? (
                    <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))" }}>
                      {(drafts[service.id]?.imageUrls || []).map((url, index) => (
                        <div key={`${index}-${url.slice(0, 20)}`} style={{ position: "relative" }}>
                          <img
                            src={url}
                            alt={`service ${index + 1}`}
                            style={{ width: "100%", aspectRatio: "1.15 / 1", objectFit: "cover", borderRadius: 14, border: "1px solid var(--border)" }}
                          />
                          <button
                            onClick={() => setDrafts((prev) => ({
                              ...prev,
                              [service.id]: {
                                ...(prev[service.id] || {}),
                                imageUrls: (prev[service.id]?.imageUrls || []).filter((_, i) => i !== index),
                              },
                            }))}
                            style={{
                              position: "absolute",
                              top: 8,
                              right: 8,
                              width: 28,
                              height: 28,
                              borderRadius: 999,
                              border: "none",
                              background: "rgba(17, 24, 39, 0.8)",
                              color: "#fff",
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ color: "var(--muted)" }}>No images uploaded for this listing yet.</div>
                  )}
                </div>

                <div style={actionRowStyle}>
                  <button
                    className="petaar-button"
                    onClick={() => saveAvailability(service.id)}
                    disabled={Boolean(uploadingByService[service.id])}
                  >
                    {uploadingByService[service.id] ? "Uploading..." : "Save Details"}
                  </button>
                  <button
                    className="petaar-button petaar-button--ghost"
                    onClick={() => handleDeleteService(service.id)}
                    disabled={deletingId === service.id}
                  >
                    {deletingId === service.id ? "Deleting..." : "Delete Service"}
                  </button>
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
